import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import UserAvatar from "../../common/avatar/UserAvatar";
import OrderDetailModal from "../../common/orderDetailModal/OrderDetailModal";
import Paginations from "../../common/pagination/Paginations";
import "./Orders.css";
import { useSelector } from "react-redux";
import { allOrderManagementApi, OrderDetailsApi, orderStatusApi } from "../../api";
import { toast } from "react-toastify";
import { useConfirm } from "../../common/confirmDialog/useConfirm";
import configuration from "../../config/Config";
import { AdminTable, ActionCell } from "../../common/adminTable";
import { formatMoney } from "../../utils/configUtils";
import StatusChip from "../../common/statusChip/StatusChip";

const Orders = () => {
  const token = useSelector((state) => state?.user?.user?.token);
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [orderManagementData, setOrderManagementData] = useState([]);
  const [orderDetailsData, setOrderDetailsData] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [myTimout, setMyTimeOut] = useState();
  const [status, setStatus] = useState("");
  const [change, setChange] = useState(false);

  const handleOrderManagementApi = async () => {
    try {
      setLoading(true);
      const response = await allOrderManagementApi({ token, searchQuery, currentPage });
      setOrderManagementData(response?.data?.orders || []);
      setCurrentPage(response?.data?.currentPage);
      setTotalPages(response?.data?.totalPages);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("Error", error);
    }
  };

  useEffect(() => {
    handleOrderManagementApi();
  }, [currentPage, change]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      handleOrderManagementApi();
    } else {
      clearTimeout(myTimout);
      const timeout = setTimeout(() => handleOrderManagementApi(), 500);
      setMyTimeOut(timeout);
      return () => clearTimeout(timeout);
    }
  }, [searchQuery]);

  const handleOrderDetails = async (id) => {
    try {
      const response = await OrderDetailsApi({ token, id });
      setOrderDetailsData(response);
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleStatusUpdate = async (newStatus, id) => {
    const isConfirmed = await confirm({ title: "Update Order Status", description: "Are you sure you want to change this order status?", confirmText: "Update" });
    if (!isConfirmed) return;
    try {
      const response = await orderStatusApi({ token, status: newStatus, id });
      toast.success(response?.message);
      setChange(!change);
    } catch (error) {
      toast.error(error?.status === 500 ? "Some error occurred" : error?.message);
    }
  };

  const handleClickOpen = (row) => {
    setSelectedRow(row);
    setStatus(row?.status || "");
    setOpen(true);
    handleOrderDetails(row?._id);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
  };

  const columns = [
    {
      field: "user",
      headerName: "Order By",
      minWidth: 160,
      renderCell: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <UserAvatar
            src={row?.user?.picture ? `${configuration.cdnFront}/${row.user.picture}` : undefined}
            name={row?.user?.fullname}
            size={32}
            sx={{ flexShrink: 0 }}
          />
          <Typography sx={{ fontFamily: "Heebo", fontSize: "13px", color: "#362B24", whiteSpace: "nowrap" }}>
            {row?.user?.fullname || "-"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "seller",
      headerName: "Business Name",
      minWidth: 160,
      renderCell: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <UserAvatar
            src={row?.seller?.picture ? `${configuration.cdnFront}/${row.seller.picture}` : undefined}
            name={row?.seller?.fullname}
            size={32}
            sx={{ flexShrink: 0 }}
          />
          <Typography sx={{ fontFamily: "Heebo", fontSize: "13px", color: "#362B24", whiteSpace: "nowrap" }}>
            {row?.seller?.fullname || "-"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "address",
      headerName: "Address",
      minWidth: 140,
      cellSx: { whiteSpace: "nowrap" },
      renderCell: (row) =>
        row?.address ? [row.address.address || ""].filter(Boolean).join(", ") : "-",
    },
    {
      field: "totalAmount",
      headerName: "Total Price",
      width: 110,
      renderCell: (row) => formatMoney(row?.totalAmount),
    },
    {
      field: "quantity",
      headerName: "Quantity",
      width: 90,
      renderCell: (row) =>
        row?.items?.reduce((total, item) => total + (item?.quantity || 0), 0),
    },
    {
      field: "paymentStatus",
      headerName: "Payment Status",
      width: 130,
      renderCell: (row) => <StatusChip value={row?.paymentStatus} />,
    },
    {
      field: "status",
      headerName: "Order Status",
      width: 110,
      renderCell: (row) => <StatusChip value={row?.status} />,
    },
    {
      field: "__actions",
      headerName: "",
      width: 60,
      align: "right",
      renderCell: (row) => (
        <ActionCell
          actions={[
            {
              label: "View",
              icon: <VisibilityIcon fontSize="small" />,
              color: "#065F46",
              onClick: () => handleClickOpen(row),
            },
          ]}
        />
      ),
    },
  ];


  return (
    <div>
      <Card sx={{ padding: "20px", borderRadius: "25px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: "20px", fontWeight: "600", color: "#362B24" }}>
            Order List
          </Typography>
          <TextField
            autoFocus
            value={searchQuery}
            onChange={(e) => {
              if (e.target.value.trim() !== "" || e.target.value === "") {
                setSearchQuery(e.target.value);
              }
            }}
            placeholder="Search by order status"
            sx={{
              width: 240,
              height: 48,
              flexShrink: 0,
              borderRadius: "33px",
              background: "#EBEBEB",
              "& .MuiOutlinedInput-root": {
                borderRadius: "33px",
                height: "100%",
                paddingRight: "10px",
              },
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#000", opacity: 0.35 }} />
                </InputAdornment>
              ),
              style: { height: "100%" },
            }}
          />
        </Box>

        <AdminTable
          columns={columns}
          rows={orderManagementData}
          loading={loading}
          rowKey="_id"
          emptyText="No orders found"
        />
        <Paginations
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={(page) => { setLoading(true); setCurrentPage(page); }}
          disabled={loading}
        />
      </Card>

      <OrderDetailModal
        open={open}
        onClose={handleClose}
        order={selectedRow}
        items={orderDetailsData?.items}
        status={status}
        onStatusChange={(newStatus) => {
          setStatus(newStatus);
          handleStatusUpdate(newStatus, selectedRow?._id);
          handleClose();
        }}
      />
    </div>
  );
};

export default Orders;
